import { Link } from "@tanstack/react-router";

import iconNavy from "@/assets/ajility-icon-navy.svg";
import iconWhite from "@/assets/ajility-icon-white.svg";
import lockupNavy from "@/assets/ajility-logo-horizontal-navy.svg";
import lockupWhite from "@/assets/ajility-logo-horizontal-white.svg";

const DEFAULT_TITLE = "Ajility, fractional COO and operations partner";

type LogoProps = {
  /** Surface tone: "light" renders the white lockup for dark surfaces. */
  tone?: "light" | "dark";
  /** "lockup" is the horizontal wordmark; "icon" is the pyramid A alone. */
  variant?: "lockup" | "icon";
  className?: string;
  linked?: boolean;
  /**
   * Accessible name. Pass "" (the default when `linked`) to mark the mark as
   * decorative so an ancestor link/heading provides the single label.
   */
  alt?: string;
  /** Kept for API compatibility with the previous raster logo. */
  priority?: boolean;
};

/**
 * Official Ajility brand mark.
 *
 * The kit files are fixed-colour vectors: never recoloured, stretched or
 * rotated here. Width is always auto so the intrinsic ratio is preserved,
 * and the wrapper carries padding equal to the icon height as clear space.
 */
export function Logo({
  tone = "light",
  variant = "lockup",
  className,
  linked = true,
  alt,
  priority = false,
}: LogoProps) {
  const resolvedAlt = alt ?? (linked ? "" : DEFAULT_TITLE);
  const src =
    variant === "icon"
      ? tone === "light"
        ? iconWhite
        : iconNavy
      : tone === "light"
        ? lockupWhite
        : lockupNavy;

  const img = (
    <img
      src={src}
      width={variant === "icon" ? 120 : 448}
      height={variant === "icon" ? 120 : 161}
      alt={resolvedAlt}
      {...(resolvedAlt === "" ? { "aria-hidden": true as const } : {})}
      decoding="async"
      loading={priority ? "eager" : "lazy"}
      draggable={false}
      className={className ? `w-auto ${className}` : "h-9 w-auto sm:h-10"}
    />
  );

  if (!linked) return img;

  return (
    <Link to="/" aria-label={DEFAULT_TITLE} className="inline-flex items-center">
      {img}
    </Link>
  );
}
